import { MouseEvent, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Link,
  Menu as MenuMUI,
  MenuItem,
  Stack,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import { Text } from '../../Text';
import { HXCIcon, Icons } from '../../HXCIcon';
import { LinkContainer } from './styles';
import useGlobalNavLinks from './useGlobalNavLinks';
import { AccountMenuList, Menu } from '../../Menu';
import type { GlobalNavProps } from './GlobalNav.types';
import { GLOBAL_NAV_HEIGHT } from '../styles';
import { CLEVER_CLINIC_MARKETING_URL, CLEVER_PATIENTS_MARKETING_URL } from 'app-core';

export const GlobalNav = ({
  linkComponent,
  activeLink,
  locationSettings,
  showAccountMenu = false,
  accountSignOut,
  isMessagesEnabled,
  isHxCustomerService,
  isHxInternalStaff,
  canViewUsers,
  userName,
}: GlobalNavProps) => {
  const [locationAnchorEl, setLocationAnchorEl] = useState<null | HTMLElement>(null);
  const [softwareAnchorEl, setSoftwareAnchorEl] = useState<null | HTMLElement>(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery((mediaTheme: Theme) => mediaTheme.breakpoints.down(720));
  const globalNavLinks = useGlobalNavLinks({ activeLink });

  const handleLocationClick = ({ currentTarget }: MouseEvent<HTMLButtonElement>) =>
    setLocationAnchorEl(currentTarget);

  const handleLocationClose = () => setLocationAnchorEl(null);

  const handleSoftwareClick = ({ currentTarget }: MouseEvent<HTMLButtonElement>) =>
    setSoftwareAnchorEl(currentTarget);

  const changeLocationHandler = (newLocation: 'uk' | 'ire') => {
    if (locationSettings?.locationOnChange) {
      locationSettings.locationOnChange(newLocation);
    }
  };

  const handleSoftwareClose = () => setSoftwareAnchorEl(null);

  const handleAccountClose = () => setAccountAnchorEl(null);

  const handleAccountClick = (event: MouseEvent<HTMLElement>) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const textStyles = useMemo(() => {
    return {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: 0,
    };
  }, []);

  const Links = useMemo(() => {
    // software has two sites so uses a dropdown
    return globalNavLinks.map((link) => (
      <LinkContainer key={link.name} active={link.active.toString()}>
        {link.name === 'Software' && (
          <>
            <Button
              aria-label="software"
              color="inherit"
              sx={{
                textTransform: 'none',
                padding: 0,
                height: '100%',
                width: '100%',
              }}
              onClick={handleSoftwareClick}
            >
              <Text variant={'p2'} color={link.active ? 'white' : 'darkGray'} sx={textStyles}>
                Software
              </Text>
            </Button>
            <MenuMUI
              id="software-menu"
              anchorEl={softwareAnchorEl}
              open={Boolean(softwareAnchorEl)}
              onClose={handleSoftwareClose}
            >
              <MenuItem onClick={handleSoftwareClose}>
                <Link
                  href={CLEVER_PATIENTS_MARKETING_URL}
                  color="inherit"
                  underline="none"
                  target="_blank"
                >
                  Clever Patients
                </Link>
              </MenuItem>
              <MenuItem onClick={handleSoftwareClose}>
                <Link
                  href={CLEVER_CLINIC_MARKETING_URL}
                  color="inherit"
                  underline="none"
                  target="_blank"
                >
                  Clever Clinic
                </Link>
              </MenuItem>
            </MenuMUI>
          </>
        )}
        {link.name !== 'Software' && (
          <Link
            href={link.href}
            color="inherit"
            underline="none"
            target="_blank"
            sx={{
              width: '100%',
              height: '100%',
            }}
          >
            <Text variant={'p2'} color={link.active ? 'white' : 'darkGray'} sx={textStyles}>
              {link.name}
            </Text>
          </Link>
        )}
      </LinkContainer>
    ));
  }, [globalNavLinks, textStyles, softwareAnchorEl]);

  return (
    <Toolbar
      data-component="GlobalNav"
      sx={{
        backgroundColor: theme.palette.header.globalNavBackground,
        height: `${GLOBAL_NAV_HEIGHT}px`,
        minHeight: `${GLOBAL_NAV_HEIGHT}px !important`,
        width: '100%',
        color: theme.palette.lightGray.main,
        display: isSmallScreen ? 'none' : 'flex',
        justifyContent: 'space-evenly',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Text variant={'p2'} color={'lightGray'} sx={{ margin: 0 }}>
          +44 (0)330 995 7999
        </Text>
      </Box>
      <Stack
        direction={'row'}
        spacing={0.8}
        justifyContent={'center'}
        sx={{
          flex: 2,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {Links}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flex: 1,
          justifyContent: 'flex-end',
          minWidth: 'fit-content',
          whiteSpace: 'nowrap',
        }}
      >
        {showAccountMenu && accountSignOut && (
          <Stack direction="row" spacing={2} alignItems="center">
            {userName && (
              <Text
                variant="p2"
                color="lightGray"
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.fontSize,
                }}
              >
                {userName}
              </Text>
            )}
            <Button
              component={linkComponent}
              to="/account/order-history"
              variant="text"
              sx={{
                color: theme.palette.white.main,
                textDecoration: 'underline',
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize,
                minWidth: 'fit-content',
                padding: 0,
                textTransform: 'capitalize',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              My Orders
            </Button>
            <Button
              variant="text"
              onClick={handleAccountClick}
              sx={{
                color: theme.palette.white.main,
                textDecoration: 'underline',
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize,
                minWidth: 'fit-content',
                padding: 0,
                textTransform: 'capitalize',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              My Account
            </Button>
            <Menu
              anchorEl={accountAnchorEl}
              anchorElId="account-menu"
              handleClose={handleAccountClose}
              menuList={
                <AccountMenuList
                  isHxCustomerService={isHxCustomerService}
                  isHxInternalStaff={isHxInternalStaff}
                  canViewUsers={canViewUsers}
                  isMessagesEnabled={isMessagesEnabled}
                  signOut={accountSignOut}
                  closeMenu={handleAccountClose}
                  linkComponent={linkComponent}
                  links={{
                    orderHistory: { url: '/account/order-history' },
                    savedOrders: { url: '/account/saved-orders' },
                    accountDetails: { url: '/account/details' },
                    users: { url: '/account/users' },
                    patients: { url: '/account/patients' },
                    addressBook: { url: '/account/address-book' },
                    invoices: { url: '/account/invoices' },
                    messages: { url: '/account/messages' },
                    events: { url: '/account/events' },
                    notes: { url: '/account/notes' },
                    validations: { url: '/account/validations' },
                    helpAndSupport: { url: '/account/help-support' },
                  }}
                />
              }
            />
          </Stack>
        )}
        {Boolean(locationSettings) && (
          <>
            <IconButton
              aria-label={`${locationSettings?.currentLocation} location`}
              color="inherit"
              onClick={handleLocationClick}
            >
              {locationSettings?.currentLocation === 'uk' ? (
                <HXCIcon name={Icons.FlagUK} />
              ) : (
                <HXCIcon name={Icons.FlagIRE} />
              )}
              <div>
                <HXCIcon name={Icons.DropdownSm} color={'white'} />
              </div>
            </IconButton>

            <MenuMUI
              id="location-menu"
              anchorEl={locationAnchorEl}
              open={Boolean(locationAnchorEl)}
              onClose={handleLocationClose}
            >
              <MenuItem onClick={() => changeLocationHandler('uk')}>
                <HXCIcon name={Icons.FlagUK} />
              </MenuItem>
              <MenuItem onClick={() => changeLocationHandler('ire')}>
                <HXCIcon name={Icons.FlagIRE} />
              </MenuItem>
            </MenuMUI>
          </>
        )}
      </Box>
    </Toolbar>
  );
};
